"use client";

/**
 * GERBANG SESI SEBELUM MENULIS SETELAN TOKO.
 *
 * ── APA YANG DIJAGA ─────────────────────────────────────────────────────────
 *
 * Log PROD 2026-09-05 (natasyashop, satu admin, satu halaman) menunjukkan POST
 * `/rest/v1/site_settings` berselang-seling: sebagian membawa `auth_user`,
 * sebagian TIDAK membawa apa pun — dan yang tanpa auth ditolak DB dengan teks
 * trigger yang tak berarti bagi admin toko.
 *
 * Gerbang ini memeriksa sesinya LEBIH DULU dan menolak di klien. Untungnya dua:
 * payload setelan tak dikirim ke jaringan sebagai anonim, dan pesannya benar
 * sejak awal alih-alih diterjemahkan dari galat Postgres di ujung sana.
 *
 * ── KENAPA `getSession`, BUKAN `getUser` ────────────────────────────────────
 *
 * `getUser()` menembak `/auth/v1/user` — satu round-trip jaringan di SETIAP
 * penyimpanan, padahal yang ingin dijawab cuma "klien ini punya sesi atau
 * tidak". `getSession()` membaca cookie/penyimpanan lokal, jadi jalur bahagianya
 * gratis. Kita memang tidak memverifikasi keaslian tokennya di sini — itu
 * pekerjaan RLS di server, dan RLS TETAP berlaku apa pun jawaban gerbang ini.
 * Gerbang ini soal PESAN dan soal tidak mengirim data percuma, bukan soal
 * keamanan; jangan pernah dijadikan satu-satunya penjaga.
 *
 * ── KENAPA TIDAK MEMBLOKIR SAAT RAGU ────────────────────────────────────────
 *
 * Kegagalan membaca sesi (`getSession` melempar) DILEWATKAN, bukan dianggap
 * "tidak ada sesi": memblokir penyimpanan karena satu blip pembacaan cookie
 * berarti admin yang sesinya sehat tak bisa menyimpan sama sekali. Biarkan
 * tulisannya jalan — kalau memang tak berkonteks, DB yang menolak dan
 * `describeSettingsWriteError` yang menerjemahkan.
 */

import type { SupabaseClient } from "@supabase/supabase-js";
import { SETTINGS_SESSION_LOST_MESSAGE } from "@/lib/settingsWriteError";

/**
 * Lempar `SETTINGS_SESSION_LOST_MESSAGE` bila klien ini TERBUKTI tak punya
 * sesi. Ragu-ragu (pembacaan sesi gagal) = lolos, lihat kepala berkas.
 */
export async function assertSettingsSession(db: SupabaseClient): Promise<void> {
  let hasSession: boolean;
  try {
    const { data } = await db.auth.getSession();
    hasSession = Boolean(data.session);
  } catch {
    return; // ragu → biarkan DB yang memutuskan
  }
  if (!hasSession) throw new Error(SETTINGS_SESSION_LOST_MESSAGE);
}
